import React, { createContext, useContext, useMemo, useEffect, useRef, useState } from "react";
import { Navigate, Outlet, useParams } from "react-router-dom";
import {
  useCaseAccess,
  type CaseAccessRole,
  type CaseAccessDeniedReason,
  type CaseDocument,
  type CaseMemberDocument,
} from "@/hooks/useCaseAccess";
import { type CaseMemberPermissions } from "@/types/caseMembership";
import { requireCaseId } from '@/lib/routeParams';
import { useAuthState } from '@/auth/AuthProvider';
import { updateCurrentCase } from '@/lib/updateCurrentCase';
import { CaseAccessContext, type CaseAccessContextValue } from '@/context/caseAccess/CaseAccessContext';

/**
 * RequireCaseAccess - Case Access Guard
 * 
 * P0 FIX (v1.13.1+): Prevents redirect-on-first-load bug
 * 
 * ISSUE: Users were redirected back to /cases after selecting a case on first load
 * ROOT CAUSE: Race condition in access checking
 * 1. User selects case → RequireCaseAccess mounts → access check starts
 * 2. Access check completes → hasAccess=true → renders dashboard
 * 3. updateCurrentCase() writes to users/{uid} → triggers useAuthUser onSnapshot
 * 4. useAuthUser re-renders → authLoading briefly becomes true
 * 5. useCaseAccess sees authLoading=true → loading=true, hasAccess=false
 * 6. RequireCaseAccess sees hasAccess=false → redirects to /cases
 * 7. Second attempt works because currentCaseId is already set (no write needed)
 * 
 * FIX: "Sticky" access state
 * - Once access is granted (hasAccess=true, loading=false), lock it in
 * - Only revoke if there's an explicit denial reason (permission-denied, not-found, etc.)
 * - Don't show loading screen during transient re-checks (prevents flash)
 * - This prevents Firestore write-triggered re-renders from causing false denials
 */

interface RequireCaseAccessProps {
  children?: React.ReactNode;
}

interface CaseContextValue {
  currentCase: CaseDocument | null;
  membership: CaseMemberDocument | null;
  userRole: CaseAccessRole | null;
  permissions: CaseMemberPermissions | null;
  hasAccess: boolean;
  deniedReason: CaseAccessDeniedReason | null;
}

const CaseContext = createContext<CaseContextValue | null>(null);

export function useCaseContext(): CaseContextValue {
  const context = useContext(CaseContext);
  if (!context) {
    throw new Error("useCaseContext must be used within a CaseProvider (RequireCaseAccess)");
  }
  return context;
}

function Loading() {
  return <div className="grid min-h-screen place-items-center text-text">Checking access…</div>;
}

function TimeoutError({ caseId, userId }: { caseId: string | undefined; userId: string | undefined }) {
  // Log a structured diagnostic blob
  useEffect(() => {
    const diagnostics = {
      caseId,
      userId,
      timestamp: new Date().toISOString(),
      message: 'Access check timed out (emulator connection or rules denial).',
    };
     
    console.error('[RequireCaseAccess] TIMEOUT', diagnostics);
  }, [caseId, userId]);
  return (
    <div className="grid min-h-screen place-items-center text-text p-4">
      <div className="max-w-md text-center">
        <h1 className="text-2xl font-bold mb-4 text-red-500">Access Check Timed Out</h1>
        <p className="text-sm text-gray-600 mb-4">
          Access check timed out (emulator connection or rules denial).<br />
          Please verify your connection and try again.
        </p>
        <button
          onClick={() => window.location.reload()}
          className="mt-4 px-4 py-2 bg-brand-orange text-white rounded"
        >
          Retry
        </button>
      </div>
    </div>
  );
}

// DEPRECATED: useCaseAccessContext is now imported from @/context/caseAccess/CaseAccessContext
// This re-export maintains backward compatibility during migration
export { useCaseAccessContext, useCasePermissions, useHasCasePermission } from '@/context/caseAccess/CaseAccessContext';
export type { CaseAccessContextValue } from '@/context/caseAccess/CaseAccessContext';

export default function RequireCaseAccess({ children }: RequireCaseAccessProps) {
  const { caseId } = useParams<{ caseId: string }>();
  const IS_DEV = import.meta.env.DEV;
  if (IS_DEV) console.log('[RequireCaseAccess] COMPONENT INVOKED', { caseId });
  requireCaseId(caseId);
  const { user } = useAuthState();
  if (IS_DEV) console.log('[RequireCaseAccess] After requireCaseId', { caseId, userId: user?.uid });
  const accessState = useCaseAccess(caseId);
  if (IS_DEV) console.log('[RequireCaseAccess] accessState:', { loading: accessState.loading, hasAccess: accessState.hasAccess, error: accessState.error?.message });

  // RACE FIX: Track whether loading has ever been true during this mount.
  // Updated synchronously during render (not in an effect) so it's set before
  // any denial check can fire — prevents showing "Access Denied" before the
  // first loading cycle completes (possible race when fromCache snapshots fire).
  const hasBeenLoadingRef = useRef(false);
  if (accessState.loading) {
    hasBeenLoadingRef.current = true;
  }

  // P0 FIX: Sticky access state prevents redirect during transient loading
  // Once access is granted, don't revoke it unless there's an explicit denial
  const [accessGranted, setAccessGranted] = useState(false);
  
  useEffect(() => {
    // Grant access when confirmed
    if (!accessState.loading && accessState.hasAccess) {
      setAccessGranted(true);
      if (IS_DEV) console.log('[RequireCaseAccess] ✅ Access granted and locked', { caseId, userId: user?.uid });
    }
    
    // Only revoke if there's an explicit denial (not just loading)
    if (!accessState.loading && !accessState.hasAccess && accessState.deniedReason) {
      setAccessGranted(false);
      if (IS_DEV) console.log('[RequireCaseAccess] ❌ Access explicitly denied', { 
        caseId, 
        userId: user?.uid, 
        reason: accessState.deniedReason 
      });
    }
  }, [accessState.loading, accessState.hasAccess, accessState.deniedReason, caseId, user?.uid]);

  // PHASE 1: Track reference changes
  const prevAccessStateRef = useRef(accessState);
  const prevPermissionsRef = useRef(accessState.permissions);
  const prevOutletContextRef = useRef<CaseAccessContextValue | null>(null);
  
  useEffect(() => {
    const prevState = prevAccessStateRef.current;
    const prevPerms = prevPermissionsRef.current;
    
    const accessStateChanged = prevState !== accessState;
    const permissionsRefChanged = prevPerms !== accessState.permissions;
    const permissionsValueChanged = JSON.stringify(prevPerms) !== JSON.stringify(accessState.permissions);
    
    if (typeof window !== 'undefined' && (import.meta.env.DEV || (window as any).Cypress)) {
      console.log('[RequireCaseAccess] 🔬 PHASE 1: Reference tracking:', {
        accessStateReferenceChanged: accessStateChanged,
        permissionsReferenceChanged: permissionsRefChanged,
        permissionsValueChanged: permissionsValueChanged,
        loading: accessState.loading,
        hasPermissions: Boolean(accessState.permissions),
        permissions: accessState.permissions,
      });
    }
    
    prevAccessStateRef.current = accessState;
    prevPermissionsRef.current = accessState.permissions;
  }, [accessState]);

  // CRITICAL FIX: Add version counter to force child re-renders when permissions load
  const [permissionsVersion, setPermissionsVersion] = useState(0);
  useEffect(() => {
    if (!accessState.loading && accessState.permissions) {
      // Increment version to force children to re-render with new permissions
      setPermissionsVersion(v => v + 1);
    }
  }, [accessState.loading, accessState.permissions]);

  // Timeout logic: fail-closed if loading > 10s
  const [timedOut, setTimedOut] = useState(false);
  const timeoutRef = useRef<number | null>(null);

  useEffect(() => {
    if (accessState.loading && !timedOut) {
      timeoutRef.current = window.setTimeout(() => {
        setTimedOut(true);
      }, 10000); // 10s
    }
    if (!accessState.loading && timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [accessState.loading, timedOut]);

  // Phase 1: Track case entry - update currentCaseId when user accesses a case
  useEffect(() => {
    if (user?.uid && caseId && accessState.hasAccess && !accessState.loading) {
      updateCurrentCase(user.uid, caseId);
    }
  }, [user?.uid, caseId, accessState.hasAccess, accessState.loading]);

  const contextValue: CaseContextValue = useMemo(
    () => ({
      currentCase: accessState.caseDoc,
      membership: accessState.membership,
      userRole: accessState.role,
      permissions: accessState.permissions,
      hasAccess: accessState.hasAccess,
      deniedReason: accessState.deniedReason,
    }),
    [
      accessState.caseDoc,
      accessState.membership,
      accessState.role,
      accessState.permissions,
      accessState.hasAccess,
      accessState.deniedReason,
    ]
  );

  // Phase 3B: React Context Provider value (replaces Outlet context)
  const caseAccessProviderValue = useMemo(
    () => {
      // Phase 3.1: Split readiness into two stages
      const accessResolved = !accessState.loading && (accessState.hasAccess || accessState.deniedReason !== null);
      const permissionsResolved = accessState.permissions !== null;
      
      return {
        caseId: caseId as string, // requireCaseId already validated this
        loading: accessState.loading,
        hasAccess: accessState.hasAccess,
        deniedReason: accessState.deniedReason,
        role: accessState.role,
        membership: accessState.membership,
        permissions: accessState.permissions,
        _version: permissionsVersion,
        accessResolved,
        permissionsResolved,
      };
    },
    [
      caseId,
      accessState.loading,
      accessState.hasAccess,
      accessState.deniedReason,
      accessState.role,
      accessState.membership,
      accessState.permissions,
      permissionsVersion,
    ]
  );
  
  // Phase 3B: Set readiness markers for Cypress/debugging
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const isReady = !accessState.loading && accessState.hasAccess && accessState.permissions !== null;
    
    if (import.meta.env.DEV || (window as any).Cypress) {
      (window as any).__TL_CASE_ACCESS_READY__ = isReady;
      
      if (isReady) {
        console.log('[RequireCaseAccess] ✅ Case access ready:', {
          caseId,
          role: accessState.role,
          permissions: accessState.permissions,
          version: permissionsVersion,
          timestamp: new Date().toISOString()
        });
      }
    }
    
    return () => {
      if (import.meta.env.DEV || (window as any).Cypress) {
        (window as any).__TL_CASE_ACCESS_READY__ = false;
      }
    };
  }, [caseId, accessState.loading, accessState.hasAccess, accessState.permissions, accessState.role, permissionsVersion]);

  if (timedOut) {
    return <TimeoutError caseId={caseId} userId={user?.uid} />;
  }

  // Phase 4: Enhanced diagnostic logging for access decisions
  if (!accessState.loading) {
    console.debug('[RequireCaseAccess] Access state resolved:', {
      caseId,
      userId: user?.uid,
      role: accessState.role,
      hasAccess: accessState.hasAccess,
      deniedReason: accessState.deniedReason,
      hasMembership: Boolean(accessState.membership),
      membershipStatus: accessState.membership?.status,
      isOwner: accessState.caseDoc && (accessState.caseDoc.ownerId === user?.uid || accessState.caseDoc.ownerUid === user?.uid),
    });
  }

  if (accessState.loading) {
    // P0 FIX: If access was previously granted, don't show loading screen during re-checks
    // This prevents the "flash" when updateCurrentCase triggers user doc updates
    if (accessGranted) {
      if (import.meta.env.DEV || (window as any).Cypress) {
        console.log('[RequireCaseAccess] Loading (access already granted, not blocking render)', { caseId });
      }
      // Continue to render - don't block on transient loading
    } else {
      // Initial load - show loading screen
      if (import.meta.env.DEV) {
        console.log('[RequireCaseAccess] Loading...', { caseId, userId: user?.uid });
      }
      return <Loading />;
    }
  }

  // Phase 7 FIX: Show error UI BEFORE checking access
  // This ensures we show user-friendly errors for connectivity issues
  if (accessState.error && accessState.error.code !== "permission-denied") {
    return (
      <div className="grid min-h-screen place-items-center text-text p-4">
        <div className="max-w-md text-center">
          <h1 className="text-2xl font-bold mb-4">Access Check Failed</h1>
          <p className="text-sm text-gray-600 mb-4">
            Unable to verify access to this case. This may be a temporary connectivity issue.
          </p>
          {import.meta.env.DEV && (
            <pre className="text-left text-xs bg-gray-100 p-2 rounded overflow-auto">
              {accessState.error.message}
            </pre>
          )}
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-brand-orange text-white rounded"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Phase 7 FIX: Handle permission-denied as access denial, not error
  // Permission-denied errors should redirect away, not show error screen
  if (accessState.deniedReason === "unauthenticated") {
    if (IS_DEV) console.warn('[RequireCaseAccess] blocking', { uid: user?.uid, caseId, memberIds: accessState.caseDoc?.memberIds, path: window?.location?.pathname });
    return <Navigate to="/login" replace />;
  }

  // P0 FIX: Only redirect if access was never granted OR explicitly denied.
  // RACE FIX: Also require that loading was observed at least once (hasBeenLoadingRef),
  // preventing denial UI from appearing during the brief race window before the
  // first Firestore snapshot arrives.
  if (!accessGranted && !accessState.loading && !accessState.hasAccess) {
    if (!hasBeenLoadingRef.current && !accessState.deniedReason) {
      // Loading never started yet AND no explicit denial — we're in the race window. Show loading spinner
      // until the actual Firestore data arrives and resolves the access decision.
      if (IS_DEV) console.log('[RequireCaseAccess] ⚠️ Suppressing premature denial (loading never observed)', { caseId, userId: user?.uid });
      return <Loading />;
    }
    if (IS_DEV) console.warn('[RequireCaseAccess] blocking', { uid: user?.uid, caseId, memberIds: accessState.caseDoc?.memberIds, path: window?.location?.pathname });
    if (IS_DEV) console.log('[RequireCaseAccess] Rendering access-denied UI', {
      reason: accessState.deniedReason,
      caseId,
      userId: user?.uid
    });
    return (
      <div className="grid min-h-screen place-items-center p-4" data-testid="case-access-denied">
        <div className="max-w-md text-center">
          <h1 className="text-2xl font-bold mb-4 text-slate-100">Access Denied</h1>
          <p className="text-sm text-gray-400 mb-6">
            {accessState.deniedReason === 'not-found'
              ? 'This case could not be found.'
              : "You don't have permission to access this case. Contact the case owner to request access."}
          </p>
          <a
            href="/cases"
            className="px-4 py-2 bg-orange-700 hover:bg-orange-800 text-white rounded-lg text-sm font-medium transition-colors"
          >
            Back to My Cases
          </a>
        </div>
      </div>
    );
  }

  // Phase 3.1: Compute readiness flags
  const accessResolved = !accessState.loading && (accessState.hasAccess || accessState.deniedReason !== null);
  const permissionsResolved = accessState.permissions !== null;
  const isFullyReady = accessResolved && accessState.hasAccess && permissionsResolved;
  
  // Phase 3.1: Always-visible state marker for debugging (hidden from UI)
  const StateMarkers = () => (
    <>
      {/* Always render - encodes full state for Cypress debugging */}
      <div
        data-testid="case-access-state"
        data-loading={String(accessState.loading)}
        data-has-access={String(accessState.hasAccess)}
        data-has-permissions={String(permissionsResolved)}
        data-role={accessState.role ?? ''}
        data-denied-reason={accessState.deniedReason ?? ''}
        data-version={String(permissionsVersion)}
        className="hidden"
      />
      {/* Phase 3.1: Stage 1 - Access decision complete */}
      {accessResolved && <div data-testid="case-access-resolved" className="hidden" />}
      {/* Phase 3.1: Stage 2 - Permissions computed */}
      {permissionsResolved && <div data-testid="case-permissions-resolved" className="hidden" />}
      {/* Legacy - full ready state (both stages complete + access granted) */}
      {isFullyReady && <div data-testid="case-access-ready" className="hidden" />}
    </>
  );
  
  if (React.isValidElement(children)) {
    if (typeof children.type !== "string") {
      return (
        <CaseContext.Provider value={contextValue}>
          <CaseAccessContext.Provider value={caseAccessProviderValue}>
            <StateMarkers />
            {React.cloneElement(children, { userRole: accessState.role })}
          </CaseAccessContext.Provider>
        </CaseContext.Provider>
      );
    }

    return (
      <CaseContext.Provider value={contextValue}>
        <CaseAccessContext.Provider value={caseAccessProviderValue}>
          <StateMarkers />
          {children}
        </CaseAccessContext.Provider>
      </CaseContext.Provider>
    );
  }

  return (
    <CaseContext.Provider value={contextValue}>
      <CaseAccessContext.Provider value={caseAccessProviderValue}>
        <StateMarkers />
        <Outlet />
      </CaseAccessContext.Provider>
    </CaseContext.Provider>
  );
}
